import dayjs from "dayjs";
import { db, projectGroupCollection } from "../../services/firebase.js";
import { getFormattedDate, isSomething } from "../../utils/utils.js";
import { ALERT_MESSAGES, GENERAL } from "./planner.constants.js";

export const formatDate = (date) => dayjs(date).format("MM/DD/YYYY");

export const formatSingleDate = (raw) => {
  const d = new Date(raw);
  return d
    .toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    })
    .replace(",", "");
};

// Helper: format a date range as “Apr 18 2025 - May 1, 2025”
export const formatDateRange = (rawRange) => {
  if (!rawRange) return "";

  // split into start / end (if there's no “ - ”, we’ll treat it as a single date)
  const [startRaw, endRaw] = rawRange.split(" - ");
  const dStart = new Date(startRaw);
  const dEnd = endRaw ? new Date(endRaw) : dStart;

  // check if they share the same year
  const sameYear = dStart.getFullYear() === dEnd.getFullYear();

  // build formatting options
  const startOpts = {
    month: "short",
    day: "numeric",
    // only include year on start if it's different
    ...(sameYear ? {} : { year: "numeric" }),
  };
  const endOpts = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };

  const start = dStart.toLocaleDateString("en-US", startOpts);
  const end = dEnd.toLocaleDateString("en-US", endOpts);

  // if there really was a range, join them; otherwise just return the single date
  return endRaw ? `${start} - ${end}` : start;
};

export const recalculateTotal = (task) => {
  const quantity = parseFloat(task.quantity) || 0;
  const cost = parseFloat(task.cost) || 0;
  const markup = parseFloat(task.markup) || 0;
  const total = quantity * cost * (1 + markup / 100);
  task.total = total.toFixed(2);
};

export const calculateTotalBudget = (data) =>
  data.reduce((total, item) => total + parseFloat(item.budget || 0), 0);

export const calculateTotalHours = (data) =>
  data.reduce((total, item) => total + parseFloat(item.hours || 0), 0);

export const getTotalCost = (newBuilderProjectCost) => {
  return newBuilderProjectCost || 0;
};

// Calculate the total task cost and total task hours for each milestone
const updatedMilestones = (milestones) => {
  return milestones.map((milestone) => {
    const updatedTasks = milestone.tasks.map((task) => ({
      ...task,
      startDate: task.startDate ? new Date(task.startDate).toISOString() : null,
      endDate: task.endDate ? new Date(task.endDate).toISOString() : null,
    }));

    const totalTaskCost = updatedTasks.reduce(
      (sum, task) => sum + parseFloat(task.budget || 0),
      0
    );
    const totalTaskHours = updatedTasks.reduce(
      (sum, task) => sum + parseFloat(task.hours || 0),
      0
    );

    return {
      ...milestone,
      tasks: updatedTasks,
      budget: totalTaskCost,
      hours: totalTaskHours,
    };
  });
};

export const hasAnyMaterialValid = (materials) =>
  materials?.some(
    (material) =>
      isSomething(material?.cost) &&
      isSomething(material?.description) &&
      isSomething(material?.quantity)
  );

export const getHasInvalidDateRanges = (milestones) =>
  milestones.some((milestone) =>
    milestone.tasks.some((task) => {
      const startDate = task.startDate ? new Date(task.startDate) : null;
      const endDate = task.endDate ? new Date(task.endDate) : null;
      return startDate && endDate && startDate > endDate;
    })
  );

export const validateDates = (milestones) => {
  let isValid = true;
  for (let milestone of milestones) {
    for (let task of milestone.tasks) {
      const hasStartDate = task.startDate;
      const hasEndDate = task.endDate;

      if (hasStartDate && !hasEndDate) {
        alert("You cannot enter a start date without an end date.");
        isValid = false;
        break; // Break out of the inner loop
      }
      if (!hasStartDate && hasEndDate) {
        alert("You cannot enter an end date without a start date.");
        isValid = false;
        break; // Break out of the inner loop
      }
    }
    if (!isValid) break; // Break out of the outer loop if invalid
  }
  return isValid;
};

export const calculateMilestones = (data) => {
  if (!data) return { updatedMilestones: [], materials: [] };
  // Prefer deliverables over timelines
  const milestones = data.milestones || [];
  const materials = data.materials || [];
  const updatedMilestones = milestones.map((milestone) => ({ ...milestone }));
  return { updatedMilestones, materials };
};

export const calculateTotalServicesCostOldBB = (milestones) => {
  const milestoneCost = milestones.reduce((milestoneSum, milestone) => {
    const tasksCost = milestone.tasks.reduce((taskSum, task) => {
      if (task?.organization === "Client") return taskSum;
      return taskSum + parseFloat(task.cost || 0); // Ensure you have a 'cost' attribute or change accordingly
    }, 0);
    return milestoneSum + tasksCost;
  }, 0);

  return milestoneCost;
};

export const getProjectDatesFromMilestones = (milestones = []) => {
  // Initialize arrays to hold parsed start and end dates
  const startDates = [];
  const endDates = [];

  // Iterate over each milestone
  milestones.forEach((milestone) => {
    if (milestone.dateRange && typeof milestone.dateRange === "string") {
      // Split the dateRange by " - " to extract start and end dates
      const parts = milestone.dateRange.split(" - ");
      if (parts.length === 2) {
        const startStr = parts[0].trim();
        const endStr = parts[1].trim();
        // Convert the strings to Date objects if valid
        const startDateObj = startStr ? new Date(startStr) : null;
        const endDateObj = endStr ? new Date(endStr) : null;
        if (startDateObj && !isNaN(startDateObj)) startDates.push(startDateObj);
        if (endDateObj && !isNaN(endDateObj)) endDates.push(endDateObj);
      }
    }
  });

  // Calculate the earliest start and latest end dates
  const plannerStartDate =
    startDates.length > 0
      ? dayjs(new Date(Math.min(...startDates))).format("MM-DD-YYYY")
      : "";
  const plannerEndDate =
    endDates.length > 0
      ? dayjs(new Date(Math.max(...endDates))).format("MM-DD-YYYY")
      : "";

  return { plannerStartDate, plannerEndDate };
};

//handleSave helper functions
const fixedFeeTypes = ["Fixed Fee Deliverable", "Fixed Fee Project"];

export const validateUserPermissions = (
  userPermissions,
  isTimelinesInvalid,
  plannerType
) => {
  if (userPermissions === GENERAL) {
    alert(ALERT_MESSAGES.General);
    return false;
  }

  if (isTimelinesInvalid) {
    let msg;
    if (plannerType === "Materials") {
      msg = ALERT_MESSAGES.SaveProjectMaterials;
    } else if (plannerType === "Time + Materials") {
      msg = ALERT_MESSAGES.SaveProjectTimeAndMaterials;
    } else if (fixedFeeTypes.includes(plannerType)) {
      msg = ALERT_MESSAGES.SaveProjectFixedFee;
    } else {
      msg = ALERT_MESSAGES.SaveProject; // fallback
    }
    window.alert(msg);
    return false;
  }

  return true;
};

// Validation: Milestones and tasks
export const validateMilestonesAndTasks = (
  milestones,
  noMilestoneProject,
  plannerType
) => {
  if (!milestones || milestones.length === 0) return true;

  // For fixed‑fee types, only require a non‑empty deliverable name
  const fixedFeeTypes = ["Fixed Fee Deliverable", "Fixed Fee Project"];
  if (fixedFeeTypes.includes(plannerType)) {
    const allHaveNames = milestones.every((m) => m.name?.trim() !== "");
    if (!allHaveNames) {
      alert(ALERT_MESSAGES.DeliverableNameRequired);
      return false;
    }
    return true;
  }

  // Check if "Responsibility" is valid
  const hasValidResponsibilities = milestones.every((milestone) =>
    milestone?.tasks?.every((task) => isSomething(task?.organization))
  );

  if (!noMilestoneProject && !hasValidResponsibilities) {
    alert(ALERT_MESSAGES.Responsibility);
    return false;
  }

  // Check for invalid milestone names
  const hasValidMilestoneNames = milestones.every(
    (milestone) => milestone.name.trim() !== ""
  );

  if (!noMilestoneProject && !hasValidMilestoneNames) {
    alert(ALERT_MESSAGES.DeliverableNameRequired);
    return false;
  }

  const hasAllTaskDescriptions = milestones.every((milestone) =>
    milestone.tasks.every(
      (task) => task.description && task.description.trim() !== ""
    )
  );

  if (!noMilestoneProject && !hasAllTaskDescriptions) {
    alert(ALERT_MESSAGES.TaskDescriptionRequired);
    return false;
  }

  // Check for invalid date ranges
  if (getHasInvalidDateRanges(milestones)) {
    alert(ALERT_MESSAGES.DateDescription);
    return false;
  }

  return true;
};

export const validateMaterials = (materials, noMilestoneProject) => {
  if (!materials || materials.length === 0) return true;

  // ✅ Check for valid expense types
  const hasValidExpenseTypes = materials.every((task) => {
    const hasName = task.description && task.description.trim() !== "";
    const hasExpenseType = task.expenseType && task.expenseType.trim() !== "";
    return !hasName || (hasName && hasExpenseType);
  });

  if (!noMilestoneProject && !hasValidExpenseTypes) {
    alert(ALERT_MESSAGES.SelectedType);
    return false;
  }

  // ✅ Check for valid descriptions if cost and quantity are present
  const hasValidMaterialDescriptions = materials.every((task) => {
    const hasCostAndQuantity = task.cost && task.quantity;
    const hasDescription = task.description && task.description.trim() !== "";
    return !hasCostAndQuantity || hasDescription;
  });

  if (!hasValidMaterialDescriptions) {
    alert(ALERT_MESSAGES.MaterialDescription);
    return false;
  }

  return true;
};

// Save Logic: Single projects
export const saveSingleProject = async ({
  id,
  plannerState,
  serviceTotal,
  statusToUpdate,
  plannerTypeToUpdate,
  templateBuilderData,
  showCongratsAlert = true,
}) => {
  const {
    projectData,
    milestones,
    materials,
    resources,
    serviceDiscount,
    materialDiscount,
    serviceDiscountUnit,
    materialDiscountUnit,
  } = plannerState;

  try {
    const projectRef = db.collection("projects").doc(id);

    await projectRef.update({
      projectName: projectData.projectName,
      projectScope: projectData.projectScope,
      plannerType: plannerTypeToUpdate || projectData.plannerType,
      plannerStartDate: projectData.plannerStartDate,
      plannerEndDate: projectData.plannerEndDate,
      status: statusToUpdate || projectData.status,
      serviceDiscount,
      materialDiscount,
      serviceDiscountUnit,
      materialDiscountUnit,
      plannerServiceValue: projectData.plannerServiceValue,
      plannerServiceHours: projectData.plannerServiceHours,
      projectTotal: projectData.projectTotal,
      rateOption: projectData.rateOption,
      deliverables: milestones,
      resources,
      materials,
      // Conditionally include builderData if it exists:
      ...(plannerState.projectObj?.builderData
        ? { builderData: plannerState.projectObj.builderData }
        : {}),
      ...(templateBuilderData ? { builderData: templateBuilderData } : {}),
    });
    if (showCongratsAlert) {
      alert(ALERT_MESSAGES.CongratulationsMsg);
    }
  } catch (error) {
    console.error("Error saving single project timelines:", error);
  }
};

export const applyDraftStatus = (project, handleStatusChange) => {
  const projectData = project?.projectData;
  const hasBudget =
    Array.isArray(project?.milestones) &&
    project.milestones.some((milestone) => milestone?.budget > 0);
  const hasPlannerServiceValue = !!projectData?.plannerServiceValue;

  if ((hasBudget || hasPlannerServiceValue) && projectData.status !== "Draft")
    handleStatusChange("Draft");
};
